import { GreenerUnit, Project, ProjectCardData } from 'types';
import { SearchResultType } from './types';

export type GlobalSearchData = {
  assets: GreenerUnit[];
  projects: Project[];
  projectsWithBookedAssets: ProjectCardData[];
  bookedAssets?: BookedAsset[];
};

type BookedAsset = {
  projectExternalIdentifier: string;
  projectName: string;
  unit: {
    greenerUnitExternalIdentifier: string;
    greenerUnitId: number;
    greenerUnitName: string;
  };
};

export const getAssetsBookedOnProject = (projectsWithBookedAssets, searchTerm): BookedAsset[] => {
  const term = searchTerm.toLowerCase();
  const bookedAssets = [] as BookedAsset[];

  projectsWithBookedAssets.forEach((project) => {
    (project.projectGreenerUnits ?? []).map((unit) => {
      if (
        unit.greenerUnitName?.toLowerCase().includes(term) ||
        unit.greenerUnitExternalIdentifier?.toLowerCase().includes(term)
      ) {
        bookedAssets.push({
          projectExternalIdentifier: project.externalIdentifier,
          projectName: project.name,
          unit,
        });
      }
    });
  });

  return bookedAssets;
};

export const distributeSearchResults = ({ searchResults, totalLimit, searchTerm }) => {
  const receivedResults = [
    { key: 'assets', items: searchResults.assets },
    { key: 'projects', items: searchResults.projects },
    { key: 'projectsWithBookedAssets', items: searchResults.projectsWithBookedAssets },
  ];

  const distributedResults = {
    assets: [],
    projects: [],
    projectsWithBookedAssets: [],
    bookedAssets: [],
  } as GlobalSearchData;
  const nonEmptyCategories = receivedResults.filter((category) => category.items.length > 0);

  if (nonEmptyCategories.length === 0) {
    return distributedResults;
  }

  const baseSlotsPerCategory = Math.floor(totalLimit / 3);
  const extraItemsToDistribute = [];

  let totalSlotsDistributed = 0;

  for (const category of receivedResults) {
    const initialSlotsCount = Math.min(category.items.length, baseSlotsPerCategory);
    distributedResults[category.key] = category.items.slice(0, initialSlotsCount);
    totalSlotsDistributed += initialSlotsCount;

    if (category.items.length > initialSlotsCount) {
      extraItemsToDistribute.push({
        key: category.key,
        items: category.items.slice(initialSlotsCount),
      });
    }
  }
  const extraSlotsToDistribute = extraItemsToDistribute.reduce((sum, category) => sum + category.items.length, 0);
  let remainingSlots = Math.min(totalLimit - totalSlotsDistributed, extraSlotsToDistribute);

  while (remainingSlots > 0) {
    for (const category of extraItemsToDistribute) {
      if (category.items.length === 0) {
        continue;
      }
      const item = category.items.shift();
      if (item) {
        distributedResults[category.key].push(item);
        remainingSlots--;
      }
      if (remainingSlots === 0) {
        break;
      }
    }
    if (extraItemsToDistribute.every((category) => category.items.length === 0)) {
      break;
    }
  }

  const maxBookedAssetsCount = distributedResults.projectsWithBookedAssets.length;

  distributedResults.bookedAssets = getAssetsBookedOnProject(
    distributedResults.projectsWithBookedAssets,
    searchTerm
  ).slice(0, maxBookedAssetsCount);

  return distributedResults;
};

export const saveRecentSearch = (
  prev: { title: string; description: string; link: string; externalIdentifier: string; type: SearchResultType }[],
  newItem: { title: string; description: string; link: string; externalIdentifier: string; type: SearchResultType }
) => {
  const filtered = prev.filter((item) => item.link !== newItem.link);
  const updated = [newItem, ...filtered];

  return updated.slice(0, 5);
};
